import {useState, Suspense, useMemo} from 'react';
// @ts-expect-error incompatibility with node16 resolution
import {Listbox} from '@headlessui/react';
import SpinnerIcon from '../icons/Spinner';
import clsx from 'clsx';
import {useServerProps} from '@shopify/hydrogen';

type SortOption = {
  collectionSortOrder: string;
  name: string;
  symbol?: string;
  productSort: {
    key?: string;
    reverse?: boolean;
  };
};

export const SORT_OPTIONS: SortOption[] = [
  {
    name: 'Artist\'s name',
    collectionSortOrder: 'MANUAL',
    productSort: {
      key: undefined,
      reverse: false,
    },
  },
  {
    name: 'Price',
    symbol: '↗',
    // name: 'Price (low to high)',
    collectionSortOrder: 'PRICE_ASC',
    productSort: {
      key: 'PRICE',
      reverse: false,
    },
  },
  {
    name: 'Price',
    symbol: '↘',
    // name: 'Price (high to low)',
    collectionSortOrder: 'PRICE_DESC',
    productSort: {
      key: 'PRICE',
      reverse: true,
    },
  },
  {
    name: 'Title',
    symbol: '↗',
    // name: 'Title (A to Z)',
    collectionSortOrder: 'ALPHA_ASC',
    productSort: {
      key: 'TITLE',
      reverse: false,
    },
  },
  {
    name: 'Title',
    symbol: '↘',
    // name: 'Title (Z to A)',
    collectionSortOrder: 'ALPHA_DESC',
    productSort: {
      key: 'TITLE',
      reverse: true,
    },
  },
  // {
  //   name: 'Best selling',
  //   collectionSortOrder: 'BEST_SELLING',
  //   productSort: {
  //     key: 'BEST_SELLING',
  //     reverse: undefined,
  //   },
  // },
  // {
  //   name: 'New arrivals',
  //   collectionSortOrder: 'CREATED',
  //   productSort: {
  //     key: 'CREATED',
  //     reverse: false,
  //   },
  // },
];

type Props = {
  initialSortOrder: string;
};

export default function SortOrderSelect({initialSortOrder}: Props) {
  // Remove 'Default' sort option if current collection is not manual / automated
  const sortOptions = useMemo(() => {
    return initialSortOrder === 'MANUAL'
      ? SORT_OPTIONS
      : SORT_OPTIONS.filter(
          (option) => option.collectionSortOrder !== 'MANUAL',
        );
  }, [initialSortOrder]);

  const [listboxOpen, setListboxOpen] = useState(false);
  const {setServerProps} = useServerProps();

  const [selectedSortOption, setSelectedSortOption] = useState(
    sortOptions.find(
      (option) => option.collectionSortOrder === initialSortOrder,
    ),
  );

  const handleChange = (sortOption: SortOption) => {
    setSelectedSortOption(sortOption);
    setServerProps('productSort', sortOption.productSort);
  };

  return (
    <Listbox onChange={handleChange} value={selectedSortOption}>
      {({open}: {open: boolean}) => {
        setTimeout(() => setListboxOpen(open));
        return (
          <div className="relative">
            <Listbox.Button className="text-left outline-0">
              <span className={clsx(
                'block text-gray',
                listboxOpen && 'text-black'
              )}>
                Sort by
              </span>
              <span className="block text-main">{selectedSortOption?.name} {selectedSortOption?.symbol && <span >{selectedSortOption.symbol}</span>}</span>
            </Listbox.Button>

            <Listbox.Options
              className={clsx(
                'absolute top-full left-0 w-full',
                'after:content-[""] after:absolute after:w-screen after:h-full after:bg-white',
                'after:top-0 after:right-0 after:-z-10 after:outline-0'
              )}
            >
              <div className="overflow-y-auto mt-20 mb-10">
                {listboxOpen && (
                  <Suspense
                    fallback={
                      <div className="overflow-hidden">
                        <SpinnerIcon />
                      </div>
                    }
                  >
                    <SortOptions
                      selectedSortOption={selectedSortOption}
                      getClassName={(active) => {
                        return clsx([
                          'cursor-pointer whitespace-nowrap text-gray',
                          active ? 'text-main' : null,
                        ]);
                      }}
                      options={sortOptions}
                    />
                  </Suspense>
                )}
              </div>
            </Listbox.Options>
          </div>
        );
      }}
    </Listbox>
  );
}

export function SortOptions({
  selectedSortOption,
  getClassName,
  options,
}: {
  selectedSortOption?: SortOption;
  getClassName: (active: boolean) => string;
  options: SortOption[];
}) {

  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: "smooth"
    });
  }

  return (
    <>
      {options
        .filter((option) => option !== selectedSortOption)
        .map((sortOption) => {
        const isSelected = sortOption === selectedSortOption;
        return (
          <Listbox.Option key={sortOption.collectionSortOrder} value={sortOption} onClick={scrollToTop}>
            {({active}: {active: boolean}) => (
              <div className={getClassName(active)}>
                <span
                  className={clsx(
                    'group',
                    isSelected && 'text-main'
                  )}
                >
                  <span className="inline-block w-[39px] group-hover:text-main">{sortOption.name}</span>
                  {sortOption.symbol && <span className="group-hover:text-main">{sortOption.symbol}</span>}
                  {/* {sortOption.symbol && <span className="group-hover:text-main">[{sortOption.symbol}]</span>} */}
                </span>
              </div>
            )}
          </Listbox.Option>
        );
      })}
    </>
  );
}